import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { useAuth } from '@/hooks/useAuth';
import { useCart } from '@/contexts/CartContext';
import { useSiteImage } from '@/hooks/useSiteImage';
import { useNavLinks } from '@/hooks/useHeaderFooterLinks';
import { useSiteDesign } from '@/hooks/useSiteDesign';
import SiteHeader from './v2/SiteHeader';

// Picks the header for the live design (Admin > Website > Settings > Design).
export default function SharedNavbar() {
  const { isNew, isPreview } = useSiteDesign();
  return isNew ? <SiteHeader preview={isPreview} /> : <ClassicNavbar />;
}

function ClassicNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/' || location.pathname === '/ko' || location.pathname === '/bn';
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const { currentUser, isAuthenticated, logout } = useAuth();
  const { totalItems, openCart } = useCart();
  const logoUrl = useSiteImage('nav.logo', 'https://public.readdy.ai/ai/img_res/f8e8e9d8-9748-4109-8994-aa9e580acb44.png');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const isActiveLink = (href: string) => {
    const fullPath = prefix + href;
    const exact = location.pathname === href || location.pathname === fullPath;
    const parent = href !== '/' && (location.pathname.startsWith(href + '/') || location.pathname.startsWith(fullPath + '/'));
    return exact || parent;
  };

  const handleScrollLink = (id: string) => {
    setMenuOpen(false);
    if (isHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(prefix + '/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 400);
    }
  };

  const rawNavLinks = useNavLinks();
  const navLinks = rawNavLinks.map((link) => ({
    id: link.id,
    label: link.label[currentLang] || link.label.en,
    href: link.href,
    isExternal: link.is_external,
  }));

  const navBg = scrolled || !isHome;
  const hamburgerColor = navBg ? 'bg-[#2A2A2A]' : 'bg-white';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          navBg ? 'bg-white/95 backdrop-blur-md border-b border-[#E8E8E6]' : 'bg-transparent'
        }`}
      >
        <div className="w-full px-6 md:px-12 h-16 flex items-center justify-between">
          {/* Left nav links */}
          <div className="hidden xl:flex items-center gap-5 2xl:gap-7">
            {navLinks.map((link) => {
              const active = !link.isExternal && isActiveLink(link.href);
              const className = `relative text-[11px] tracking-[0.1em] 2xl:tracking-[0.15em] uppercase font-normal transition-colors cursor-pointer whitespace-nowrap py-1 ${
                active
                  ? 'text-[#7B1113] font-medium'
                  : navBg
                    ? 'text-[#2A2A2A] hover:text-[#7B1113]'
                    : 'text-white hover:text-white/70'
              }`;
              const content = (
                <>
                  {link.label}
                  {active && <span className="absolute -bottom-0.5 left-0 w-full h-[2px] bg-[#7B1113] rounded-full" />}
                </>
              );
              return link.isExternal ? (
                <a key={link.id} href={link.href} target="_blank" rel="noopener noreferrer" className={className}>
                  {content}
                </a>
              ) : (
                <Link key={link.id} to={prefix + link.href} className={className}>
                  {content}
                </Link>
              );
            })}
          </div>

          {/* Center logo */}
          <div className="absolute left-1/2 -translate-x-1/2 flex items-center">
            <Link to={prefix + '/'} className="flex items-center whitespace-nowrap">
              <img
                src={logoUrl}
                alt="Rx.K Seoul"
                className="h-8 md:h-9 w-auto"
              />
            </Link>
          </div>

          {/* Right side */}
          <div className="hidden xl:flex items-center gap-5">
            <LanguageSwitcher />

            {isAuthenticated ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors cursor-pointer ${
                    navBg ? 'hover:bg-[#F0EFED]' : 'hover:bg-white/10'
                  }`}
                >
                  <div className="w-7 h-7 rounded-full bg-[#7B1113] flex items-center justify-center text-[10px] text-white font-medium">
                    {currentUser?.name?.charAt(0)}
                  </div>
                  <span
                    className={`text-xs hidden md:inline ${navBg ? 'text-[#2A2A2A]' : 'text-white'}`}
                  >
                    {currentUser?.name}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-xs ${navBg ? 'text-[#8A8A8A]' : 'text-white/70'}`}
                  ></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl border border-[#E8E8E6] shadow-lg py-2 z-50">
                    <div className="px-4 py-3 border-b border-[#E8E8E6]">
                      <p className="text-sm font-medium text-[#2A2A2A]">{currentUser?.name}</p>
                      <p className="text-xs text-[#8A8A8A]">{currentUser?.email}</p>
                    </div>
                    <Link
                      to={prefix + '/my/profile'}
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#2A2A2A] hover:bg-[#F0EFED] transition-colors"
                    >
                      <i className="ri-user-line text-[#8A8A8A]"></i> {t('my.profile.title')}
                    </Link>
                    <Link
                      to={prefix + '/my/orders'}
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#2A2A2A] hover:bg-[#F0EFED] transition-colors"
                    >
                      <i className="ri-shopping-bag-line text-[#8A8A8A]"></i> {t('my.orders.title')}
                    </Link>
                    <button
                      onClick={() => {
                        logout();
                        setDropdownOpen(false);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#7B1113] hover:bg-[#7B1113]/5 transition-colors cursor-pointer"
                    >
                      <i className="ri-logout-box-r-line"></i> {t('my.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to={prefix + '/login'}
                  className={`text-xs tracking-wider uppercase transition-colors cursor-pointer whitespace-nowrap ${
                    navBg
                      ? 'text-[#2A2A2A] hover:text-[#7B1113]'
                      : 'text-white hover:text-white/70'
                  }`}
                >
                  {t('auth.login.title', 'Sign In')}
                </Link>
                <Link
                  to={prefix + '/products'}
                  className={`px-5 py-2 bg-[#7B1113] text-white text-[11px] tracking-[0.12em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer whitespace-nowrap ${
                    navBg ? '' : 'shadow-lg'
                  }`}
                >
                  {t('common.shopNow')}
                </Link>
              </>
            )}

            <button
              onClick={openCart}
              className={`relative w-8 h-8 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                navBg ? 'hover:bg-[#F0EFED]' : 'hover:bg-white/10'
              }`}
              aria-label={t('nav.cart')}
            >
              <i className={`ri-shopping-bag-line ${navBg ? 'text-[#2A2A2A]' : 'text-white'}`}></i>
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#7B1113] text-white text-[9px] font-bold flex items-center justify-center">
                  {totalItems > 9 ? '9+' : totalItems}
                </span>
              )}
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="xl:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <div className="relative w-5 h-5">
              <span
                className={`absolute left-0 w-5 h-[2px] rounded-full transition-all duration-300 ${
                  menuOpen ? `top-[9px] rotate-45 ${hamburgerColor}` : `top-[3px] ${hamburgerColor}`
                }`}
              />
              <span
                className={`absolute left-0 top-[9px] w-5 h-[2px] rounded-full transition-all duration-300 ${
                  menuOpen ? 'opacity-0 scale-0' : `opacity-100 scale-100 ${hamburgerColor}`
                }`}
              />
              <span
                className={`absolute left-0 w-5 h-[2px] rounded-full transition-all duration-300 ${
                  menuOpen ? `top-[9px] -rotate-45 ${hamburgerColor}` : `top-[15px] ${hamburgerColor}`
                }`}
              />
            </div>
          </button>
        </div>
      </nav>

      {/* Mobile menu overlay */}
      <div
        className={`fixed inset-0 z-[49] bg-black/40 transition-opacity duration-300 xl:hidden ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMenuOpen(false)}
      />

      {/* Mobile menu slide-in panel */}
      <div
        className={`fixed top-0 right-0 z-[49] h-full w-[280px] bg-white shadow-2xl transition-transform duration-300 ease-out xl:hidden flex flex-col ${
          menuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-16 flex items-center justify-end px-6">
          <button
            onClick={() => setMenuOpen(false)}
            className="w-10 h-10 flex items-center justify-center cursor-pointer"
            aria-label="Close menu"
          >
            <i className="ri-close-line text-xl text-[#2A2A2A]"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-2 flex flex-col gap-5">
          {navLinks.map((link) => {
            const active = !link.isExternal && isActiveLink(link.href);
            const className = `relative text-sm tracking-[0.12em] uppercase font-normal transition-colors w-fit py-1 ${
              active ? 'text-[#7B1113] font-medium' : 'text-[#2A2A2A] hover:text-[#7B1113]'
            }`;
            const content = (
              <>
                {link.label}
                {active && <span className="absolute -bottom-0.5 left-0 w-full h-[2px] bg-[#7B1113] rounded-full" />}
              </>
            );
            return link.isExternal ? (
              <a key={link.id} href={link.href} target="_blank" rel="noopener noreferrer" onClick={() => setMenuOpen(false)} className={className}>
                {content}
              </a>
            ) : (
              <Link key={link.id} to={prefix + link.href} onClick={() => setMenuOpen(false)} className={className}>
                {content}
              </Link>
            );
          })}

          <div className="border-t border-[#E8E8E6] pt-5 flex flex-col gap-4">
            {isAuthenticated ? (
              <>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#7B1113] flex items-center justify-center text-[11px] text-white font-medium">
                    {currentUser?.name?.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#2A2A2A]">{currentUser?.name}</p>
                    <p className="text-xs text-[#8A8A8A]">{currentUser?.email}</p>
                  </div>
                </div>
                <Link
                  to={prefix + '/my'}
                  onClick={() => setMenuOpen(false)}
                  className="text-sm text-[#2A2A2A] hover:text-[#7B1113] transition-colors"
                >
                  {t('my.dashboard', 'Dashboard')}
                </Link>
                <Link
                  to={prefix + '/my/orders'}
                  onClick={() => setMenuOpen(false)}
                  className="text-sm text-[#2A2A2A] hover:text-[#7B1113] transition-colors"
                >
                  {t('my.orders.title')}
                </Link>
                <button
                  onClick={() => {
                    logout();
                    setMenuOpen(false);
                  }}
                  className="text-left text-sm text-[#7B1113] hover:text-[#5C0D0F] transition-colors cursor-pointer"
                >
                  {t('my.logout')}
                </button>
              </>
            ) : (
              <>
                <Link
                  to={prefix + '/login'}
                  onClick={() => setMenuOpen(false)}
                  className="text-sm text-[#2A2A2A] hover:text-[#7B1113] transition-colors"
                >
                  {t('auth.login.title', 'Sign In')}
                </Link>
                <Link
                  to={prefix + '/register'}
                  onClick={() => setMenuOpen(false)}
                  className="text-sm text-[#7B1113] hover:text-[#5C0D0F] transition-colors"
                >
                  {t('auth.register.title', 'Create Account')}
                </Link>
              </>
            )}
          </div>

          <div className="mt-auto pt-6 pb-8 border-t border-[#E8E8E6] flex items-center justify-between">
            <LanguageSwitcher />
            <button
              onClick={() => {
                setMenuOpen(false);
                openCart();
              }}
              className="relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F0EFED] cursor-pointer"
            >
              <i className="ri-shopping-bag-line text-lg text-[#2A2A2A]"></i>
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#7B1113] text-white text-[9px] font-bold flex items-center justify-center">
                  {totalItems > 9 ? '9+' : totalItems}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}